import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { utilizationSettingsService as service } from '../../_services/utilizationSettings.service';
import { groupMachinesByCategory } from '../../utils/utilizationCategories';
import { CategoryMachinesModal } from './CategoryMachinesModal';

const sortNames = (names) => [...names].sort((left, right) => left.localeCompare(right));
const sameNames = (left, right) => left.length === right.length && left.every((name) => right.includes(name));
const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

// A draft matches the saved settings when the same categories and machines are
// chosen and the hours are equal.
const sameSettings = (draft, saved) => sameNames(draft.categories, saved.categories)
    && sameNames(draft.excludedMachines, saved.excludedMachines)
    && Number(draft.hours) === saved.availableHours;
const toDraft = (settings) => ({
    categories: settings.categories,
    excludedMachines: settings.excludedMachines,
    hours: String(settings.availableHours),
});

function validHours(value) {
    const hours = Number(value);
    return value !== '' && Number.isFinite(hours) && hours > 0 && hours <= 24;
}

// Categories from the company's machines, plus saved names that no longer
// match any machine, so an admin can still untick them.
function buildOptions(machines, savedNames) {
    const groups = new Map(groupMachinesByCategory(machines).map((group) => [group.name, group.machines]));
    (savedNames || []).forEach((name) => { if (!groups.has(name)) groups.set(name, []); });
    return sortNames(Array.from(groups.keys())).map((name) => ({ name, machines: groups.get(name) }));
}

const countedMachines = (option, excludedMachines) => option.machines
    .filter((machine) => !excludedMachines.includes(machine.machine)).length;

function machinesLabel(option, excludedMachines) {
    const total = option.machines.length;
    const counted = countedMachines(option, excludedMachines);
    return counted === total ? plural(total, 'machine') : `${counted} of ${plural(total, 'machine')}`;
}

function UtilitiesManagement({ company }) {
    const [machines, setMachines] = useState(null);
    const [saved, setSaved] = useState(null);
    const [draft, setDraft] = useState({ categories: [], excludedMachines: [], hours: '' });
    const [machinesDialog, setMachinesDialog] = useState(null);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const savedRef = useRef(null);
    const mounted = useRef(true);
    const successTimer = useRef(0);

    const applySaved = (settings) => {
        const previous = savedRef.current;
        savedRef.current = settings;
        setSaved(settings);
        // Keep the admin's unsaved edits; only follow the server when nothing was changed.
        setDraft((current) => (previous === null || sameSettings(current, previous) ? toDraft(settings) : current));
    };

    useEffect(() => () => {
        mounted.current = false;
        window.clearTimeout(successTimer.current);
    }, []);

    useEffect(() => {
        if (!company) return undefined;
        let active = true;

        const machinesRequest = service.getMachines(company);
        if (machinesRequest.data) setMachines(machinesRequest.data);
        machinesRequest.revalidate
            .then((rows) => { if (active) setMachines(rows); })
            .catch((err) => {
                if (!active) return;
                setMachines((current) => current || []);
                setError(err.message || 'Unable to load machines.');
            });

        const settingsRequest = service.getSettings();
        if (settingsRequest.data) applySaved(settingsRequest.data);
        settingsRequest.revalidate
            .then((settings) => { if (active) applySaved(settings); })
            .catch((err) => { if (active) setError(err.message || 'Unable to load utilization settings.'); });

        return () => { active = false; };
    }, [company]);

    const toggle = (name) => {
        setSuccess(false);
        setDraft((current) => ({
            ...current,
            categories: current.categories.includes(name)
                ? current.categories.filter((entry) => entry !== name)
                : [...current.categories, name],
        }));
    };

    // Replace this category's choices: its unticked machines join the excluded list.
    const applyMachines = (option, tickedNames) => {
        const names = option.machines.map((machine) => machine.machine);
        setSuccess(false);
        setDraft((current) => ({
            ...current,
            excludedMachines: [
                ...current.excludedMachines.filter((name) => !names.includes(name)),
                ...names.filter((name) => !tickedNames.includes(name)),
            ],
        }));
        setMachinesDialog(null);
    };

    const changeHours = (value) => {
        setSuccess(false);
        setDraft((current) => ({ ...current, hours: value }));
    };

    const save = async () => {
        const previous = saved;
        const next = {
            categories: sortNames(draft.categories),
            availableHours: Number(draft.hours),
            excludedMachines: sortNames(draft.excludedMachines),
        };
        setSaving(true);
        setSuccess(false);
        setError('');
        // Optimistic: show the new settings as saved, roll back if the server refuses them.
        savedRef.current = next;
        setSaved(next);

        try {
            const confirmed = await service.saveSettings(next);
            if (!mounted.current) return;
            savedRef.current = confirmed;
            setSaved(confirmed);
            setDraft(toDraft(confirmed));
            setSuccess(true);
            successTimer.current = window.setTimeout(() => {
                if (mounted.current) setSuccess(false);
            }, 3000);
        } catch (err) {
            if (!mounted.current) return;
            savedRef.current = previous;
            setSaved(previous);
            setError(err.message || 'Failed to save utilities settings. Please try again.');
        } finally {
            if (mounted.current) setSaving(false);
        }
    };

    const cancel = () => {
        if (saved) setDraft(toDraft(saved));
        setError('');
    };

    const options = machines === null ? null : buildOptions(machines, saved && saved.categories);
    const loadingSaved = saved === null;
    const hoursValid = validHours(draft.hours);
    const dirty = !loadingSaved && !sameSettings(draft, saved);
    const countedOptions = (options || [])
        .filter((option) => draft.categories.length === 0 || draft.categories.includes(option.name));
    const countedMachineCount = countedOptions
        .reduce((total, option) => total + countedMachines(option, draft.excludedMachines), 0);

    return (
        <section className="mt-5" aria-labelledby="utilities-management-title">
            <div style={{ marginBottom: '16px' }}>
                <h4 id="utilities-management-title">Utilities Management</h4>
                <p className="text-muted mb-0">
                    Choose what the Floor Deck Utilization card and its Machine Categories window count.
                </p>
            </div>

            {success && (
                <Alert variant="success" onClose={() => setSuccess(false)} dismissible>
                    Utilities settings saved!
                </Alert>
            )}

            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            <Card className="mb-3">
                <Card.Body>
                    <h5 className="mb-1">Machine categories</h5>
                    <p className="text-muted small mb-3">If none are ticked, all categories are counted. Click the machine count to choose machines.</p>
                    {options === null ? (
                        <div className="text-center py-3">
                            <Spinner animation="border" size="sm" /> Loading machine categories…
                        </div>
                    ) : options.length === 0 ? (
                        <p className="text-muted mb-0">No machines found for this company.</p>
                    ) : (
                        <>
                            <Row>
                                {options.map((option, index) => (
                                    <Col key={option.name} xs={12} md={6} lg={4} className="mb-2 d-flex align-items-center gap-1">
                                        <Form.Check
                                            className="mb-0"
                                            type="checkbox"
                                            id={`utilization-category-${index}`}
                                            label={option.name}
                                            checked={draft.categories.includes(option.name)}
                                            disabled={loadingSaved || saving}
                                            onChange={() => toggle(option.name)}
                                        />
                                        {option.machines.length ? (
                                            <Button
                                                variant="link"
                                                className="p-0 align-baseline"
                                                disabled={loadingSaved || saving}
                                                aria-label={`Choose ${option.name} machines, ${machinesLabel(option, draft.excludedMachines)} counted`}
                                                onClick={() => setMachinesDialog(option)}
                                            >
                                                ({machinesLabel(option, draft.excludedMachines)})
                                            </Button>
                                        ) : (
                                            <span className="text-muted">(no machines)</span>
                                        )}
                                    </Col>
                                ))}
                            </Row>
                            <p className="text-muted small mt-2 mb-0" role="status">
                                {loadingSaved
                                    ? 'Loading saved selection…'
                                    : `${draft.categories.length === 0
                                        ? 'All categories are counted'
                                        : `${plural(draft.categories.length, 'category', 'categories')} selected`} · ${plural(countedMachineCount, 'machine')} counted.`}
                            </p>
                        </>
                    )}
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Body>
                    <h5 className="mb-1">Utilization by Time</h5>
                    <p className="text-muted small mb-3">
                        Utilization by Time = Operating time ÷ (number of machines × scheduled available time) × 100.
                        Operating time is planned production time minus unplanned stop time, the same as in the daily OEE.
                    </p>
                    <Form.Group controlId="utilization-available-hours" style={{ maxWidth: '320px' }}>
                        <Form.Label>Scheduled available time (hours per machine per day)</Form.Label>
                        <Form.Control
                            type="number"
                            min="0.1"
                            max="24"
                            step="0.1"
                            value={draft.hours}
                            disabled={loadingSaved || saving}
                            isInvalid={!loadingSaved && !hoursValid}
                            onChange={(event) => changeHours(event.target.value)}
                        />
                        <Form.Control.Feedback type="invalid">Enter a number more than 0 and at most 24.</Form.Control.Feedback>
                        <Form.Text muted>Default is 22 hours (24 hours minus 2 hours lunch break).</Form.Text>
                    </Form.Group>
                </Card.Body>
            </Card>

            {machinesDialog && (
                <CategoryMachinesModal
                    category={machinesDialog.name}
                    machines={machinesDialog.machines}
                    excludedMachines={draft.excludedMachines}
                    onApply={(tickedNames) => applyMachines(machinesDialog, tickedNames)}
                    onClose={() => setMachinesDialog(null)}
                />
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
                <Button variant="primary" onClick={save} disabled={!dirty || !hoursValid || saving}>
                    {saving ? 'Saving…' : 'Save Utilities'}
                </Button>
                <Button variant="secondary" onClick={cancel} disabled={!dirty || saving}>
                    Cancel
                </Button>
            </div>
        </section>
    );
}

export { UtilitiesManagement };
